import React, { useState } from "react"
import axios from "axios"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"

function RequestList({ requests, onRequestUpdate, onRequestDelete, onBulkAction }) {
    const [filterType, setFilterType] = useState("all")
    const [filterStatus, setFilterStatus] = useState("all")
    const [selectedRequests, setSelectedRequests] = useState([])
    // Admin Note Modal State
    const [showNoteModal, setShowNoteModal] = useState(false)
    const [noteAction, setNoteAction] = useState(null) // 'approve' or 'reject'
    const [noteRequestId, setNoteRequestId] = useState(null)
    const [adminNote, setAdminNote] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)

    const getStatusColor = (status) => {
        switch(status) { 
            case "pending": return "status-pending"; 
            case "approved": return "status-approved"; 
            case "rejected": return "status-rejected"; 
            default: return "status-pending" 
        }
    }

    const getStatusIcon = (status) => {
        switch(status) { 
            case "pending": return "fa-clock"; 
            case "approved": return "fa-check-circle"; 
            case "rejected": return "fa-times-circle"; 
            default: return "fa-clock" 
        }
    }

    const getRequestTypeIcon = (type) => type === "Swap" ? "fa-exchange-alt" : "fa-calendar-day"
    const getRequestTypeClass = (type) => type === "Swap" ? "type-swap" : "type-off"

    // Open note modal before approve/reject
    const openNoteModal = (id, action) => {
        setNoteRequestId(id)
        setNoteAction(action)
        setAdminNote("")
        setShowNoteModal(true)
    }

    // Submit with note
    const handleActionWithNote = async () => {
        if (!noteRequestId || !noteAction) return
        
        setIsSubmitting(true)
        try {
            const token = localStorage.getItem("adminToken")
            const status = noteAction === 'approve' ? 'approved' : 'rejected'
            await axios.put(
                `${API_URL}/api/requests/${noteRequestId}/status`,
                { status, admin_note: adminNote },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            setShowNoteModal(false)
            setNoteRequestId(null)
            setNoteAction(null)
            setAdminNote("")
            onRequestUpdate()
        } catch (error) { 
            console.error("Error updating request:", error); 
            alert("Failed to update request") 
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleApprove = async (id) => {
        // Open modal instead of directly approving
        openNoteModal(id, 'approve')
    }

    const handleReject = async (id) => {
        // Open modal instead of directly rejecting
        openNoteModal(id, 'reject')
    }

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this request?")) {
            try {
                const token = localStorage.getItem("adminToken")
                await axios.delete(`${API_URL}/api/requests/${id}`, { headers: { Authorization: `Bearer ${token}` } })
                if (onRequestDelete) onRequestDelete(id)
            } catch (error) { 
                console.error("Error deleting request:", error); 
                alert("Failed to delete request") 
            }
        }
    }

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = filteredRequests.map(r => r.id)
            setSelectedRequests(allIds)
        } else {
            setSelectedRequests([])
        }
    }

    const handleSelectOne = (id) => {
        setSelectedRequests(prev => {
            if (prev.includes(id)) {
                return prev.filter(i => i !== id)
            } else {
                return [...prev, id]
            }
        })
    }

    const handleBulkApprove = async () => {
        if (selectedRequests.length === 0) {
            alert("Please select at least one request")
            return
        }
        if (window.confirm(`Approve ${selectedRequests.length} selected request(s)?`)) {
            const token = localStorage.getItem("adminToken")
            let success = 0
            let failed = 0
            for (const id of selectedRequests) {
                try {
                    await axios.put(`${API_URL}/api/requests/${id}/status`, 
                        { status: "approved", admin_note: "Bulk approved" }, 
                        { headers: { Authorization: `Bearer ${token}` } }
                    )
                    success++
                } catch (error) {
                    failed++
                    console.error("Error approving request:", error)
                }
            }
            setSelectedRequests([])
            onBulkAction ? onBulkAction() : onRequestUpdate()
            alert(`✅ ${success} request(s) approved!${failed > 0 ? ` ❌ ${failed} failed.` : ''}`)
        }
    }

    const handleBulkReject = async () => {
        if (selectedRequests.length === 0) {
            alert("Please select at least one request")
            return
        }
        if (window.confirm(`Reject ${selectedRequests.length} selected request(s)?`)) {
            const token = localStorage.getItem("adminToken")
            let success = 0
            let failed = 0
            for (const id of selectedRequests) {
                try {
                    await axios.put(`${API_URL}/api/requests/${id}/status`, 
                        { status: "rejected", admin_note: "Bulk rejected" }, 
                        { headers: { Authorization: `Bearer ${token}` } }
                    )
                    success++
                } catch (error) {
                    failed++
                    console.error("Error rejecting request:", error)
                }
            }
            setSelectedRequests([])
            onBulkAction ? onBulkAction() : onRequestUpdate()
            alert(`✅ ${success} request(s) rejected!${failed > 0 ? ` ❌ ${failed} failed.` : ''}`)
        }
    }

    const handleBulkDelete = async () => {
        if (selectedRequests.length === 0) {
            alert("Please select at least one request")
            return
        }
        if (window.confirm(`Delete ${selectedRequests.length} selected request(s)? This cannot be undone!`)) {
            const token = localStorage.getItem("adminToken")
            let success = 0
            let failed = 0
            for (const id of selectedRequests) {
                try {
                    await axios.delete(`${API_URL}/api/requests/${id}`, { headers: { Authorization: `Bearer ${token}` } })
                    success++
                } catch (error) {
                    failed++
                    console.error("Error deleting request:", error)
                }
            }
            setSelectedRequests([])
            onBulkAction ? onBulkAction() : onRequestUpdate()
            alert(`✅ ${success} request(s) deleted!${failed > 0 ? ` ❌ ${failed} failed.` : ''}`)
        }
    }

    const filteredRequests = requests.filter(request => {
        let match = true
        if (filterType !== "all") match = match && request.request_type === filterType
        if (filterStatus !== "all") match = match && request.status === filterStatus
        return match
    })

    if (requests.length === 0) {
        return (
            <div className="no-requests">
                <i className="fas fa-plane"></i>
                <p>No requests yet</p>
            </div>
        )
    }

    return (
        <div className="request-list-container">
            {/* Admin Note Modal */}
            {showNoteModal && (
                <div className="modal-overlay" onClick={() => setShowNoteModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>{noteAction === 'approve' ? '✅ Approve Request' : '❌ Reject Request'}</h3>
                        <p className="modal-subtitle">Add a note (optional):</p>
                        <textarea
                            className="modal-textarea"
                            value={adminNote}
                            onChange={(e) => setAdminNote(e.target.value)}
                            placeholder="Enter your reason for approving/rejecting..."
                            rows="4"
                        />
                        <div className="modal-actions">
                            <button 
                                className="modal-cancel-btn" 
                                onClick={() => setShowNoteModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className={`modal-confirm-btn ${noteAction === 'approve' ? 'approve' : 'reject'}`}
                                onClick={handleActionWithNote}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Processing...' : `✅ Confirm ${noteAction === 'approve' ? 'Approve' : 'Reject'}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="filter-bar">
                <div className="filter-group">
                    <label><i className="fas fa-filter"></i> Type</label>
                    <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="filter-select">
                        <option value="all">All Types</option>
                        <option value="Swap"><i className="fas fa-exchange-alt"></i> SWAP</option>
                        <option value="Off"><i className="fas fa-calendar-day"></i> OFF</option>
                    </select>
                </div>
                <div className="filter-group">
                    <label><i className="fas fa-tag"></i> Status</label>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
                        <option value="all">All Status</option>
                        <option value="pending"><i className="fas fa-clock"></i> Pending</option>
                        <option value="approved"><i className="fas fa-check-circle"></i> Approved</option>
                        <option value="rejected"><i className="fas fa-times-circle"></i> Rejected</option>
                    </select>
                </div>
            </div>

            {filteredRequests.length > 0 && (
                <div className="bulk-actions-bar">
                    <div className="bulk-select-all">
                        <label className="checkbox-label">
                            <input 
                                type="checkbox" 
                                checked={selectedRequests.length === filteredRequests.length && filteredRequests.length > 0}
                                onChange={handleSelectAll}
                            />
                            <span>Select All ({filteredRequests.length})</span>
                        </label>
                    </div>
                    <div className="bulk-actions">
                        <span className="bulk-count">{selectedRequests.length} selected</span>
                        <button 
                            className="bulk-approve-btn" 
                            onClick={handleBulkApprove}
                            disabled={selectedRequests.length === 0}
                        >
                            <i className="fas fa-check"></i> Bulk Approve
                        </button>
                        <button 
                            className="bulk-reject-btn" 
                            onClick={handleBulkReject}
                            disabled={selectedRequests.length === 0}
                        >
                            <i className="fas fa-times"></i> Bulk Reject
                        </button>
                        <button 
                            className="bulk-delete-btn" 
                            onClick={handleBulkDelete}
                            disabled={selectedRequests.length === 0}
                        >
                            <i className="fas fa-trash"></i> Bulk Delete
                        </button>
                    </div>
                </div>
            )}
            
            <div className="request-list">
                {filteredRequests.map(request => (
                    <div key={request.id} className={`request-card-minimal ${selectedRequests.includes(request.id) ? 'selected' : ''}`}>
                        <div className="request-card-left">
                            <div className="request-checkbox">
                                <input 
                                    type="checkbox" 
                                    checked={selectedRequests.includes(request.id)}
                                    onChange={() => handleSelectOne(request.id)}
                                />
                            </div>
                        </div>
                        <div className="request-card-content">
                            <div className="card-header-row">
                                <div className="card-left-badges">
                                    <div className={`request-type-badge ${getRequestTypeClass(request.request_type)}`}>
                                        <i className={`fas ${getRequestTypeIcon(request.request_type)}`}></i> {request.request_type}
                                    </div>
                                    <div className={`request-status ${getStatusColor(request.status)}`}>
                                        <i className={`fas ${getStatusIcon(request.status)}`}></i> {request.status}
                                    </div>
                                </div>
                                <span className="request-id"><i className="fas fa-hashtag"></i> {request.id}</span>
                            </div>
                            
                            <div className="card-details-grid">
                                <div className="detail-item">
                                    <span className="detail-label"><i className="fas fa-calendar-alt"></i> Date(s)</span>
                                    <span className="detail-value">
                                        {request.request_date && request.request_date.includes(',') 
                                            ? request.request_date.split(',').map(d => 
                                                new Date(d).toLocaleDateString("en-US", { 
                                                    month: "short", 
                                                    day: "numeric", 
                                                    year: "numeric" 
                                                })
                                              ).join(' | ')
                                            : new Date(request.request_date).toLocaleDateString("en-US", { 
                                                weekday: "short", 
                                                year: "numeric", 
                                                month: "short", 
                                                day: "numeric" 
                                              })
                                        }
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label"><i className="fas fa-user"></i> Name</span>
                                    <span className="detail-value">{request.employee_name}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label"><i className="fas fa-id-card"></i> Employee ID</span>
                                    <span className="detail-value">{request.employee_id}</span>
                                </div>
                                {request.duty_code && (
                                    <div className="detail-item">
                                        <span className="detail-label"><i className="fas fa-code"></i> Duty Code</span>
                                        <span className="detail-value">{request.duty_code}</span>
                                    </div>
                                )}
                                {request.swap_with && (
                                    <div className="detail-item">
                                        <span className="detail-label"><i className="fas fa-exchange-alt"></i> Swap With</span>
                                        <span className="detail-value">{request.swap_with}</span>
                                    </div>
                                )}
                                {request.admin_note && (
                                    <div className="detail-item">
                                        <span className="detail-label"><i className="fas fa-sticky-note"></i> Admin Note</span>
                                        <span className="detail-value" style={{ color: '#ab6272', fontStyle: 'italic' }}>
                                            "{request.admin_note}"
                                        </span>
                                    </div>
                                )}
                            </div>
                            
                            {request.remarks && (
                                <div className="card-remarks">
                                    <i className="fas fa-comment"></i>
                                    <span>{request.remarks}</span>
                                </div>
                            )}
                            
                            <div className="card-meta">
                                <i className="fas fa-paperclip"></i>
                                {new Date(request.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                                {request.updated_at && request.updated_at !== request.created_at && (
                                    <span> • <i className="fas fa-edit"></i> {new Date(request.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
                                )}
                            </div>
                            
                            <div className="card-actions">
                                {request.status === "pending" && (
                                    <div className="action-buttons">
                                        <button className="approve-btn" onClick={() => handleApprove(request.id)}>
                                            <i className="fas fa-check"></i> Approve
                                        </button>
                                        <button className="reject-btn" onClick={() => handleReject(request.id)}>
                                            <i className="fas fa-times"></i> Reject
                                        </button>
                                    </div>
                                )}
                                <button className="delete-btn" onClick={() => handleDelete(request.id)}>
                                    <i className="fas fa-trash"></i> Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default RequestList